import { createApi } from '@reduxjs/toolkit/query/react';
import { createSentryFetchBaseQuery } from '../../../../app/api/sentryFetchBaseQuery';

// --- Response types (api_logs / dashboard) ---

/** GET /logs/summary */
export interface LogsSummaryMetrics {
  total_calls: number;
  avg_latency_ms: number | null;
  max_latency_ms: number | null;
  total_projects: number;
  unique_users: number;
  high_alerts: number | null;
}

export interface HourlyBucket {
  hour: string;
  call_count: number;
  avg_latency_ms: number;
}

export interface RiskScoreBucket {
  hour: string;
  bias: number;
  fairness: number;
  prompt_injection: number;
  pii_privacy: number;
  threshold: number;
}

export interface CostByProjectRow {
  project_name: string;
  call_count: number;
  estimated_cost: number;
}

export interface AlertSeverityRow {
  severity: string;
  alert_count: number;
}

export interface AlertControlRow {
  control: string;
  alert_count: number;
}

export type AlertLogSeverity = 'Critical' | 'High' | 'Medium' | 'Low';
export type AlertLogStatus = 'Open' | 'Acknowledged' | 'Resolved';

export interface AlertLogRow {
  id: number;
  time: string;
  project_name: string;
  user_id: string;
  api_name: string;
  latency_ms: number;
  severity: AlertLogSeverity;
  control: string;
  trigger: string;
  status: AlertLogStatus;
}

export interface ApiLogRow {
  id: number;
  user_id: string;
  project_id: string;
  log_time: string;
  logout_time: string;
  api_name: string;
  response_time_ms: number;
}

export interface TimeRangeQueryParams {
  days?: number;
}

export interface AlertLogsQueryParams {
  limit?: number;
  offset?: number;
  project_id?: string;
  /** Critical | High | Medium | Low; omit or "All" for no filter */
  severity?: string;
}

export interface ApiLogsQueryParams {
  limit?: number;
  offset?: number;
}

/** API maxima per backend contract */
export const LOGS_ALERT_PAGE_SIZE_MAX = 200;
export const LOGS_RAW_PAGE_SIZE_MAX = 500;

interface LogsDataResponse<T> {
  status: number;
  data: T;
}

export interface LogsPaginatedResponse<T> {
  status: number;
  total: number;
  limit?: number;
  offset?: number;
  data: T[];
}

interface LogsMessageResponse {
  status: number;
  message: string;
}

interface LogsErrorResponse {
  error: string;
}

export const logsApi = createApi({
  reducerPath: 'logsApi',
  baseQuery: createSentryFetchBaseQuery({
    baseUrl: `${process.env.REACT_APP_API_BASE_URL}/`
  }),
  tagTypes: ['LogsSummary', 'LogsHourly', 'LogsRiskScores', 'LogsCost', 'LogsAlerts', 'LogsAlertTable', 'LogsRaw'],
  endpoints: builder => ({
    getLogsSummary: builder.query<LogsSummaryMetrics, void>({
      query: () => 'logs/summary',
      transformResponse: (response: LogsDataResponse<LogsSummaryMetrics>) => response.data,
      providesTags: ['LogsSummary']
    }),

    getLogsHourly: builder.query<HourlyBucket[], TimeRangeQueryParams | void>({
      query: params => ({
        url: 'logs/hourly',
        ...(params?.days != null ? { params: { days: params.days } } : {})
      }),
      transformResponse: (response: LogsDataResponse<HourlyBucket[]>) => response.data ?? [],
      providesTags: ['LogsHourly']
    }),

    getLogsRiskScores: builder.query<RiskScoreBucket[], TimeRangeQueryParams | void>({
      query: params => ({
        url: 'logs/risk-scores',
        ...(params?.days != null ? { params: { days: params.days } } : {})
      }),
      transformResponse: (response: LogsDataResponse<RiskScoreBucket[]>) => response.data ?? [],
      providesTags: ['LogsRiskScores']
    }),

    getLogsCostByProject: builder.query<CostByProjectRow[], void>({
      query: () => 'logs/cost-by-project',
      transformResponse: (response: LogsDataResponse<CostByProjectRow[]>) => response.data ?? [],
      providesTags: ['LogsCost']
    }),

    getLogsAlertsSeverity: builder.query<AlertSeverityRow[], void>({
      query: () => 'logs/alerts/severity',
      transformResponse: (response: LogsDataResponse<AlertSeverityRow[]>) => response.data ?? [],
      providesTags: ['LogsAlerts']
    }),

    getLogsAlertsControl: builder.query<AlertControlRow[], void>({
      query: () => 'logs/alerts/control',
      transformResponse: (response: LogsDataResponse<AlertControlRow[]>) => response.data ?? [],
      providesTags: ['LogsAlerts']
    }),

    getLogsAlertLogs: builder.query<LogsPaginatedResponse<AlertLogRow>, AlertLogsQueryParams | void>({
      query: params => {
        const limit = Math.min(params?.limit ?? 50, LOGS_ALERT_PAGE_SIZE_MAX);
        const severity = params?.severity?.trim();
        const severityFilter = severity && severity !== 'All' ? { severity } : {};
        return {
          url: 'logs/alerts/logs',
          params: {
            limit,
            offset: params?.offset ?? 0,
            ...(params?.project_id ? { project_id: params.project_id } : {}),
            ...severityFilter
          }
        };
      },
      providesTags: ['LogsAlertTable']
    }),

    getLogs: builder.query<LogsPaginatedResponse<ApiLogRow>, ApiLogsQueryParams | void>({
      query: params => ({
        url: 'logs/logs',
        params: {
          limit: Math.min(params?.limit ?? 50, LOGS_RAW_PAGE_SIZE_MAX),
          offset: params?.offset ?? 0
        }
      }),
      providesTags: ['LogsRaw']
    }),

    getLogById: builder.query<ApiLogRow, number>({
      query: logId => `logs/logs/${logId}`,
      transformResponse: (response: LogsDataResponse<ApiLogRow>) => response.data,
      providesTags: (_result, _error, id) => [{ type: 'LogsRaw', id }]
    }),

    deleteLogById: builder.mutation<LogsMessageResponse, number>({
      query: logId => ({
        url: `logs/logs/${logId}`,
        method: 'DELETE'
      }),
      invalidatesTags: [
        'LogsSummary',
        'LogsHourly',
        'LogsRiskScores',
        'LogsCost',
        'LogsAlerts',
        'LogsAlertTable',
        'LogsRaw'
      ]
    })
  })
});

export const {
  useGetLogsSummaryQuery,
  useGetLogsHourlyQuery,
  useGetLogsRiskScoresQuery,
  useGetLogsCostByProjectQuery,
  useGetLogsAlertsSeverityQuery,
  useGetLogsAlertsControlQuery,
  useGetLogsAlertLogsQuery,
  useGetLogsQuery,
  useGetLogByIdQuery,
  useDeleteLogByIdMutation
} = logsApi;

export type { LogsErrorResponse };
